// Cliente del API de Busco: guarda tokens en localStorage y renueva la sesión
// automáticamente con el refresh token cuando el access token vence.
export const API_BASE = process.env.NEXT_PUBLIC_API_URL ?? "/api";
const API = API_BASE;

const ACCESS_KEY = "busco.access";
const REFRESH_KEY = "busco.refresh";
// A qué pantalla va "Inicio" para este usuario. Se cachea para que tocar el
// logo no tenga que esperar a /auth/me (ese viaje era el parpadeo de landing).
export const INICIO_KEY = "busco.inicio";

// Lleva el status HTTP para que las pantallas distingan "no autenticado" (401)
// de "sin permiso" (403) o "falló el servidor" (5xx) y no expulsen al login por error.
export class ApiError extends Error {
  constructor(message: string, readonly status: number) {
    super(message);
    this.name = "ApiError";
  }
}

export function isAuthError(e: unknown) {
  return e instanceof ApiError && e.status === 401;
}

export function setTokens(access: string, refresh: string) {
  localStorage.setItem(ACCESS_KEY, access);
  localStorage.setItem(REFRESH_KEY, refresh);
  // Cambió la identidad (login normal o salto de rol en /accesos): el inicio
  // cacheado era del usuario anterior y mandaría al módulo equivocado.
  localStorage.removeItem(INICIO_KEY);
}

export function clearTokens() {
  localStorage.removeItem(ACCESS_KEY);
  localStorage.removeItem(REFRESH_KEY);
  localStorage.removeItem(INICIO_KEY);
  // El socket queda autenticado como el usuario anterior: hay que cerrarlo
  // o el siguiente que ingrese en este dispositivo vería sus mensajes.
  closeSocketOnLogout?.();
}

// Registrado por lib/socket.ts para evitar un import circular
let closeSocketOnLogout: (() => void) | null = null;
export function onLogout(fn: () => void) {
  closeSocketOnLogout = fn;
}

export function hasSession() {
  return typeof window !== "undefined" && !!localStorage.getItem(ACCESS_KEY);
}

// Nuestros errores propios ya vienen en español, pero los que arma Nest solo
// (o nginx delante) llegan en inglés y el usuario los ve tal cual: "Internal
// server error" en el celular de un técnico no significa nada y parece que la
// app se rompió para siempre.
const EN_ESPANOL: Record<string, string> = {
  "internal server error": "Tuvimos un problema de nuestro lado. Probá de nuevo en un momento.",
  "bad gateway": "El servidor no responde. Probá de nuevo en un momento.",
  "service unavailable": "El servicio no está disponible ahora mismo. Probá de nuevo en unos minutos.",
  "gateway timeout": "El servidor tardó demasiado en responder. Probá de nuevo.",
  "request timeout": "El servidor tardó demasiado en responder. Probá de nuevo.",
  unauthorized: "Tu sesión venció. Ingresá de nuevo.",
  forbidden: "No tenés permiso para hacer esto.",
  "not found": "No encontramos lo que buscabas.",
  "bad request": "Los datos que enviaste no son válidos.",
  conflict: "Eso ya existe o cambió mientras trabajabas. Actualizá y probá de nuevo.",
  "payload too large": "El archivo es demasiado pesado.",
  "unsupported media type": "Ese tipo de archivo no está permitido.",
  "too many requests": "Demasiados intentos. Esperá un momento y probá de nuevo.",
  "failed to fetch": "No pudimos conectarnos. Revisá tu conexión y probá de nuevo.",
};

export const ERROR_DE_RED =
  "No pudimos conectarnos. Revisá tu conexión y probá de nuevo.";

const GENERICO_5XX =
  "Tuvimos un problema de nuestro lado. Probá de nuevo en un momento.";

function errorMessage(body: unknown, status: number): string {
  let crudo = "";
  if (body && typeof body === "object" && "message" in body) {
    const m = (body as { message: string | string[] }).message;
    crudo = Array.isArray(m) ? m.join(". ") : String(m ?? "");
  }
  // Un 5xx nunca lleva un texto pensado para el usuario: sea lo que sea lo que
  // haya dicho el servidor (o el proxy, en HTML), mostramos algo entendible.
  if (status >= 500) return GENERICO_5XX;
  const traducido = EN_ESPANOL[crudo.trim().toLowerCase()];
  if (traducido) return traducido;
  return crudo.trim() || "Algo salió mal, probá de nuevo";
}

async function rawFetch(path: string, options: RequestInit = {}) {
  const token = localStorage.getItem(ACCESS_KEY);
  const headers = new Headers(options.headers);
  if (token) headers.set("Authorization", `Bearer ${token}`);
  // Con FormData el navegador arma el Content-Type (boundary incluido)
  if (options.body && !(options.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }
  // Sin señal (o con el servidor caído) fetch tira un TypeError "Failed to
  // fetch" que terminaba impreso en pantalla en inglés. Lo convertimos en un
  // ApiError con status 0: no es 401, así que ninguna pantalla desloguea.
  try {
    return await fetch(`${API}${path}`, { ...options, headers });
  } catch {
    throw new ApiError(ERROR_DE_RED, 0);
  }
}

// Una sola renovación en vuelo: varias llamadas en paralelo con el token vencido
// esperan la misma promesa en vez de pisarse escribiendo en localStorage.
let refreshing: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    const refresh = localStorage.getItem(REFRESH_KEY);
    if (!refresh) return false;
    let res: Response;
    try {
      res = await fetch(`${API}/auth/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refreshToken: refresh }),
      });
    } catch {
      // Sin red no se puede saber si la sesión sigue viva: se conserva.
      return false;
    }
    // Solo borramos la sesión si el servidor la rechaza; un 502 durante un
    // deploy no debe desloguear a nadie.
    if (res.status === 401 || res.status === 403) {
      clearTokens();
      return false;
    }
    if (!res.ok) return false;
    const data = await res.json();
    setTokens(data.accessToken, data.refreshToken);
    return true;
  })().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

export async function api<T = unknown>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  let res = await rawFetch(path, options);
  if (res.status === 401 && (await tryRefresh())) {
    res = await rawFetch(path, options);
  }
  const body = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(errorMessage(body, res.status), res.status);
  return body as T;
}

// Los archivos privados (fotos de tickets, documentos KYC) se sirven por el API
// con el token en la cabecera, así que no se pueden poner directo en un <img src>:
// se descargan y se convierten en URL de objeto local.
export async function apiBlobUrl(path: string): Promise<string> {
  let res = await rawFetch(path);
  if (res.status === 401 && (await tryRefresh())) {
    res = await rawFetch(path);
  }
  if (!res.ok) throw new ApiError("No se pudo abrir el archivo", res.status);
  return URL.createObjectURL(await res.blob());
}
